// Pure routing decision for the patient switchable-view URL (`?patientView=`), kept free of React/hook
// imports so it is unit-tested directly (no hook harness, no transitive module chain).

// View names that could become allowed once user data and feature flags have loaded — used to tell "still
// loading, wait" from "rewrite to the fallback". (NOT "each role's primary view": liveCalls/greyfinch are
// nobody's default, they're flag-gated additions.)
export const KNOWN_VIEWS = ['copilot', 'chat', 'notes', 'liveCalls', 'greyfinch']

export const PatientViewAction = { Dispatch: 'dispatch', Wait: 'wait', Rewrite: 'rewrite' }

// Reconcile a requested view against the currently-allowed set:
//   - Dispatch: it's allowed now → activate it.
//   - Wait: a known view not yet allowed while its gating inputs are still loading → leave the URL alone.
//   - Rewrite: an unknown view, OR a known view still absent AFTER inputs loaded (a disabled feature flag
//     is permanently false, a plan role the user lacks never appears) → rewrite to the fallback, so a
//     flag-off `?patientView=greyfinch` link can't leave the URL and UI disagreeing forever.
export const resolvePatientViewAction = ({ requestedView, allowedViews, viewInputsLoaded }) => {
  if (allowedViews.includes(requestedView)) return PatientViewAction.Dispatch
  if (KNOWN_VIEWS.includes(requestedView) && !viewInputsLoaded) return PatientViewAction.Wait
  return PatientViewAction.Rewrite
}

// "View inputs loaded" = everything that builds allowedViews has settled, so a still-absent KNOWN view is
// a resolved-off flag (→ rewrite) rather than mid-load (→ wait). For a regular doctor that's the doctor
// profile (→ feature flags, which arrive atomically with it) plus billing (→ plan role / grinPlanKey).
//
// Admin-role VIEWERS (isUserOfAnyAdminRole — the whole cohort: Admin, OpsAdmin, DevAdmin, DsoSupport,
// Distributor, MultiPracticeManagers, HI, GI, Sales) are excluded. Their plan comes from the context
// practice, set synchronously, while that practice's feature flags load separately — and for six of those
// nine roles `mpFetchPracticeFFs` never even fires (it gates on isHI/isDSO/single-role Admin), so the
// context FFs never arrive. If we treated grinPlanKey alone as "loaded", a gated deep-link would be
// rewritten away before (or without ever) the flags resolving. Excluding admin viewers keeps them on the
// wait path — the SAME behavior origin/staging had for everyone (this change only NARROWS it for regular
// doctors), so it is not a regression; admin viewers simply don't get the flag-off desync auto-correct.
// NOTE(smoke-test): the sidebar bird button dispatches into allowedViews directly, so a DevAdmin with the
// greyfinchIntegration flag ON still reaches the panel via the button (this only affects URL deep-links).
export const areViewInputsLoaded = ({ doctorId, grinPlanKey, isAdminViewer }) =>
  Boolean(doctorId) && Boolean(grinPlanKey) && !isAdminViewer
